import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useT } from "../i18n/LanguageContext";

const FACEBOOK_URL = "https://www.facebook.com/provalo.eu/";
const INSTAGRAM_URL = "https://www.instagram.com/provalospiedinificio/";
const WHATSAPP_NUMBER = "393347049226";
const WA_CHANNEL_URL = "https://www.whatsapp.com/channel/0029Vb7Anch1Hsq0C9bMi43T";

const waMessages = {
  it: "Ciao, vorrei avere qualche informazione.",
  en: "Hi, I'd like some information.",
  es: "Hola, me gustaría tener alguna información.",
  fr: "Bonjour, j'aimerais avoir quelques informations.",
  de: "Hallo, ich hätte gerne ein paar Informationen.",
};

// SVG icons — subdued original brand colors
const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <defs>
      <linearGradient id="ig-grad-soft" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#A8533F" />
        <stop offset="30%" stopColor="#B14572" />
        <stop offset="60%" stopColor="#8E3D8E" />
        <stop offset="100%" stopColor="#3E5A9C" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-grad-soft)" />
    <rect x="5.5" y="5.5" width="13" height="13" rx="4" fill="none" stroke="#fff" strokeWidth="1.6" />
    <circle cx="12" cy="12" r="3.2" fill="none" stroke="#fff" strokeWidth="1.6" />
    <circle cx="16.4" cy="7.6" r="0.95" fill="#fff" />
  </svg>
);

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <circle cx="12" cy="12" r="11" fill="#2B4A7E" />
    <path
      d="M13.5 12h2l.3-2.4h-2.3V8.1c0-.7.2-1.2 1.2-1.2h1.2V4.8c-.2 0-.9-.1-1.8-.1-1.8 0-3 1.1-3 3.1V9.6H9v2.4h2.1V19h2.4v-7z"
      fill="#fff"
    />
  </svg>
);

const WhatsAppIcon = () => (
  <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
    <circle cx="12" cy="12" r="11" fill="#1FAE54" />
    <path
      d="M16.7 14.1c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.6.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1-.8-.3-1.5-.8-2.1-1.4-.5-.5-1-1.1-1.3-1.8-.1-.2 0-.4.1-.5.1-.1.2-.3.4-.4.1-.1.2-.2.3-.4.1-.2 0-.3 0-.4 0-.1-.5-1.3-.7-1.7-.2-.5-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.2.2-.7.7-.7 1.7 0 1 .8 2 .9 2.2.1.1 1.6 2.5 4 3.5 1.4.5 1.9.6 2.5.5.4 0 1.3-.5 1.5-1.1.2-.5.2-1 .1-1.1l-.4-.3z"
      fill="#fff"
    />
  </svg>
);

const SocialPill = ({ href, icon, label, color, glow, testId }) => (
  <motion.a
    href={href} target="_blank" rel="noopener noreferrer"
    whileHover={{ y: -3 }}
    transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
    className="group flex items-center gap-3 px-4 py-3 rounded-full border bg-[rgba(255,255,255,0.02)] transition-all duration-300"
    style={{
      borderColor: `${color}40`,
      boxShadow: `0 6px 18px ${glow}`,
    }}
    data-testid={testId}
    aria-label={label}
  >
    <span className="flex-shrink-0">{icon}</span>
    <span className="font-medium tracking-wider uppercase text-[12px] text-[var(--pn-cream)]/80 group-hover:text-[var(--pn-milk)] transition-colors">
      {label}
    </span>
  </motion.a>
);

export const SocialFollow = () => {
  const { t, lang } = useT();
  const x = t.social;
  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMessages[lang] || waMessages.it)}`;

  return (
    <section
      className="relative pt-[60px] md:pt-[90px] lg:pt-[110px] pb-[36px] md:pb-[56px] lg:pb-[68px] px-6 md:px-14 lg:px-20 overflow-hidden bg-noir"
      data-testid="social-follow-section"
    >
      <div className="absolute inset-0 grain pointer-events-none opacity-50" />
      <div className="relative max-w-[1100px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Seguici */}
          <div>
            <motion.span
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="eyebrow"
            >
              {x.followEyebrow}
            </motion.span>
            <motion.h3
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
              className="mt-5 font-display font-light text-[26px] md:text-[34px] leading-[1.15] text-[var(--pn-milk)]"
            >
              {x.followTitle_a}
              {" "}
              <em className="italic text-[var(--pn-amber)]">{x.followTitle_b}</em>
            </motion.h3>

            <motion.div
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.9 }}
              className="mt-7 flex flex-wrap gap-3"
            >
              <SocialPill
                href={INSTAGRAM_URL}
                icon={<InstagramIcon />}
                label="Instagram"
                color="#B14572"
                glow="rgba(177, 69, 114, 0.22)"
                testId="social-instagram"
              />
              <SocialPill
                href={FACEBOOK_URL}
                icon={<FacebookIcon />}
                label="Facebook"
                color="#2B4A7E"
                glow="rgba(43, 74, 126, 0.28)"
                testId="social-facebook"
              />
              <SocialPill
                href={waUrl}
                icon={<WhatsAppIcon />}
                label="WhatsApp"
                color="#1FAE54"
                glow="rgba(31, 174, 84, 0.24)"
                testId="social-whatsapp"
              />
            </motion.div>
          </div>

          {/* Canale WhatsApp */}
          <div>
            <motion.span
              initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="eyebrow"
              style={{ color: "#1FAE54" }}
            >
              {x.channelEyebrow}
            </motion.span>
            <motion.h3
              initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.9, ease: [0.22, 0.61, 0.36, 1] }}
              className="mt-5 font-display font-light text-[26px] md:text-[34px] leading-[1.15] text-[var(--pn-milk)]"
            >
              {x.channelTitle_a}{" "}
              <em className="italic" style={{ color: "#1FAE54" }}>{x.channelTitle_b}</em>
            </motion.h3>

            <motion.a
              href={WA_CHANNEL_URL} target="_blank" rel="noopener noreferrer"
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.9 }}
              whileHover={{ y: -3 }}
              className="mt-7 group flex items-center gap-4 p-4 md:p-5 rounded-2xl bg-[rgba(31,174,84,0.06)] border border-[#1FAE54]/35 transition-all duration-300 hover:bg-[rgba(31,174,84,0.10)] hover:border-[#1FAE54]/60"
              style={{ boxShadow: "0 10px 28px rgba(31, 174, 84, 0.16)" }}
              data-testid="cta-whatsapp-channel"
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: "linear-gradient(180deg, #2DD96E 0%, #1AAE52 100%)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3)" }}
              >
                <WhatsAppIcon />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display text-[17px] md:text-[19px] text-[var(--pn-milk)] leading-tight">
                  {x.channelJoin}
                </p>
                <p className="mt-1 text-[13px] md:text-[14px] text-[var(--pn-cream)]/55 font-light leading-snug">
                  {x.channelSub}
                </p>
              </div>
              <ArrowUpRight size={20} strokeWidth={1.6}
                className="text-[#1FAE54] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform flex-shrink-0"
              />
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
};
